import {
  Dialog,
  DialogButtons,
  DialogContent,
  DialogMessageBox,
  DialogTitle,
} from '@verantos-dev/shared-components';

import { useExportDownloadModal } from './useExportDownloadModal';
import type { ExportDownload } from '../../api/downloadExport';

export interface ExportDownloadModalMessages {
  title: string;
  loadingTitle: string;
  loadingText: string;
  errorTitle: string;
  errorText: string;
  retryButtonLabel: string;
}

export interface ExportDownloadModalProps {
  /** Prefix for the dialog's element ids. */
  id: string;
  download: ExportDownload;
  messages: ExportDownloadModalMessages;
  onClose: () => void;
}

const DIALOG_PAPER_SX = { width: 600, maxHeight: '80%', margin: 0 } as const;

/**
 * Fetches `download` on mount and saves the file, showing progress, then the
 * error with a Retry button if the fetch fails. Closes itself once the file saves.
 */
export const ExportDownloadModal = ({
  id,
  download,
  messages,
  onClose,
}: ExportDownloadModalProps) => {
  const { isSubmitting, submittingError, onRetry } = useExportDownloadModal({ onClose, download });

  const inError = !!submittingError && !isSubmitting;

  // A download is non-destructive, so the dialog is always dismissible — closing
  // mid-fetch just hides the progress UI (any in-flight save still completes).
  return (
    <Dialog
      id={id}
      open
      PaperProps={{ sx: DIALOG_PAPER_SX }}
      fullWidth
      maxWidth="lg"
      onClose={onClose}
    >
      <DialogTitle id={`${id}-title`} showClose onClose={onClose}>
        {messages.title}
      </DialogTitle>
      <DialogContent>
        {inError ? (
          <DialogMessageBox
            id={`${id}-error`}
            title={messages.errorTitle}
            type="error"
            text={messages.errorText}
          />
        ) : (
          <DialogMessageBox
            id={`${id}-loading`}
            title={messages.loadingTitle}
            type="loading"
            text={messages.loadingText}
          />
        )}
      </DialogContent>
      {inError && (
        <DialogButtons
          id={`${id}-buttons`}
          saveButton={{ label: messages.retryButtonLabel, onClick: onRetry }}
        />
      )}
    </Dialog>
  );
};
